import { DashboardFilters } from "@/components/dashboard/dashboard-filters";
import { PortalTileTitle } from "@/components/portal-tile-title";
import { ContextHelpButton } from "@/components/registrant/help/context-help-button";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  getLicenseTypesOptions,
  getRegistrantAccountOptions,
} from "@/hera-api/client/@tanstack/react-query.gen";
import { useAuthQuerySetup } from "@/hooks/use-auth-query-setup";
import type { RegistrantOption } from "@/hooks/use-registrant-dashboard-data";
import type { HelpPortalId } from "@/lib/help-portal";
import { filterAvailableLicenseTypes } from "@/lib/license-type-utils";
import type { HelpArticleId } from "@/lib/registrant-help";
import { cn } from "@/lib/utils";
import type { JurisdictionOption, LicenseTypeOption } from "@/types";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ClipboardList, CreditCard, PlusCircle } from "lucide-react";

function ComplianceIssuesTile({
  count,
  onView,
  className,
}: {
  count: number;
  onView?: () => void;
  className?: string;
}) {
  return (
    <Card
      className={cn(
        "border-2 border-destructive bg-destructive/15 text-destructive",
        className,
      )}
    >
      <CardHeader className="pb-2">
        <PortalTileTitle
          title="Compliance Issues"
          className="text-lg text-destructive"
        />
      </CardHeader>
      <CardContent className="pt-0 flex flex-col gap-2">
        <p className="text-sm font-medium text-destructive">
          {count} propert{count === 1 ? "y" : "ies"} with a compliance issue
        </p>
        {onView ? (
          <Button
            variant="outline"
            className="w-full border-destructive text-destructive hover:bg-destructive/10 hover:text-destructive"
            type="button"
            onClick={() => onView()}
          >
            <ClipboardList className="h-4 w-4 mr-2" />
            View properties
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
}

function InspectionPendingTile({
  count,
  onView,
  className,
}: {
  count: number;
  onView?: () => void;
  className?: string;
}) {
  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <PortalTileTitle title="Inspection Pending" className="text-lg" />
      </CardHeader>
      <CardContent className="pt-0 flex flex-col gap-2">
        <p className="text-sm text-muted-foreground">
          {count} propert{count === 1 ? "y" : "ies"} waiting for inspection
        </p>
        {onView ? (
          <Button
            variant="outline"
            className="w-full"
            type="button"
            onClick={() => onView()}
          >
            <ClipboardList className="h-4 w-4 mr-2" />
            View properties
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
}

interface DashboardHeaderProps {
  /** Context help article for the current dashboard tab. */
  helpArticleId?: HelpArticleId;
  /** Help catalog and Help Center links. Defaults to the registrant portal. */
  helpPortal?: HelpPortalId;
  showRegisterButton?: boolean;
  /** Registrations with Balance Due display status */
  balanceDueCount?: number;
  /** Late-renewal registrations. Compliance issues are a separate tile. */
  needsAttentionCount?: number;
  /** Active registrations with an open or ready-for-check compliance issue */
  complianceIssueCount?: number;
  /** Registrations scheduled to be paid later (Bulk Payment tab cohort) */
  bulkPaymentPayLaterCount?: number;
  /** Compliant registrations whose newest inspection is Inspection Pending */
  inspectionPendingCount?: number;
  /** Switch registrant dashboard workspace tab (e.g. from tile actions) */
  onSelectWorkspaceTab?: (tab: string) => void;
  /** Open Balance Due tab */
  onViewBalanceDueRegistrations?: () => void;
  /** Open Needs Attention tab */
  onViewNeedsAttentionRegistrations?: () => void;
  /** Open Compliance Issues tab */
  onViewComplianceIssueRegistrations?: () => void;
  /** Open All Active with status filters for the Bulk Payment tile cohort */
  onViewBulkPaymentPayLaterRegistrations?: () => void;
  /** Open All Active filtered to Inspection Pending */
  onViewInspectionPendingRegistrations?: () => void;
  selectedJurisdictions?: string[];
  onSelectedJurisdictionsChange?: (jurisdictions: string[]) => void;
  jurisdictionOptions?: JurisdictionOption[];
  selectedLicenseTypes?: string[];
  onSelectedLicenseTypesChange?: (licenseTypes: string[]) => void;
  licenseTypeOptions?: LicenseTypeOption[];
  shouldShowLicenseTypeFilter?: boolean;
  /** When true (company with >1 user), show registrant filter and column */
  hasMultipleRegistrantAccounts?: boolean;
  registrantOptions?: RegistrantOption[];
  selectedRegistrantAccountId?: string | null;
  onSelectedRegistrantAccountIdChange?: (id: string | null) => void;
  /** Column filters for license table (enables Clear all to reset them) */
  columnFilters?: Record<string, string[]>;
  onColumnFiltersChange?: (filters: Record<string, string[]>) => void;
  registrantBalanceDueCohortActive?: boolean;
  onClearRegistrantBalanceDueCohort?: () => void;
  registrantBulkPaymentPayLaterCohortActive?: boolean;
  onClearRegistrantBulkPaymentPayLaterCohort?: () => void;
  selectedAms?: string | null;
  onSelectedAmsChange?: (ams: string | null) => void;
  amsOptions?: Array<{ value: string; label: string }>;
  /** Keep the dashboard title visible while its surrounding list pane scrolls. */
  stickyTitle?: boolean;
  /** Hide register / pay-now / pay-later CTAs (OMT/AMS portal). */
  hidePaymentActions?: boolean;
  /** Hide the Balance Due tile (OMT/AMS portal). */
  hideBalanceDue?: boolean;
}

export function DashboardHeader({
  helpArticleId = "dashboard",
  helpPortal = "registrant",
  showRegisterButton = true,
  balanceDueCount = 0,
  needsAttentionCount = 0,
  complianceIssueCount = 0,
  bulkPaymentPayLaterCount = 0,
  inspectionPendingCount = 0,
  onSelectWorkspaceTab,
  onViewBalanceDueRegistrations,
  onViewNeedsAttentionRegistrations,
  onViewComplianceIssueRegistrations,
  onViewBulkPaymentPayLaterRegistrations,
  onViewInspectionPendingRegistrations,
  selectedJurisdictions,
  onSelectedJurisdictionsChange,
  jurisdictionOptions,
  selectedLicenseTypes,
  onSelectedLicenseTypesChange,
  licenseTypeOptions,
  shouldShowLicenseTypeFilter = false,
  hasMultipleRegistrantAccounts = false,
  registrantOptions = [],
  selectedRegistrantAccountId = null,
  onSelectedRegistrantAccountIdChange,
  columnFilters,
  onColumnFiltersChange,
  registrantBalanceDueCohortActive = false,
  onClearRegistrantBalanceDueCohort,
  registrantBulkPaymentPayLaterCohortActive = false,
  onClearRegistrantBulkPaymentPayLaterCohort,
  selectedAms = null,
  onSelectedAmsChange,
  amsOptions = [],
  stickyTitle = false,
  hidePaymentActions = false,
  hideBalanceDue = false,
}: DashboardHeaderProps) {
  const { isAuthSetupComplete } = useAuthQuerySetup();

  const { data: licenseTypesData, isLoading: areLicenseTypesLoading } =
    useQuery({
      ...getLicenseTypesOptions(),
      enabled: isAuthSetupComplete && showRegisterButton,
    });

  const { data: registrantAccount } = useQuery({
    ...getRegistrantAccountOptions(),
    enabled: isAuthSetupComplete && !hidePaymentActions,
  });
  const canMakePayments =
    !hidePaymentActions && Boolean(registrantAccount?.can_make_payments);

  // Filter out license types that are under development or deleted
  const licenseTypes = filterAvailableLicenseTypes(
    licenseTypesData?.license_types,
  );

  // Determine display name:
  // For secondary accounts, use primary account's company_name (if set) or name
  // For primary accounts, use company_name for companies, otherwise use name
  const displayName = registrantAccount?.primary_id
    ? registrantAccount.primary_account_company_name ||
      registrantAccount.primary_account_name
    : registrantAccount?.company_name || registrantAccount?.name;
  const dashboardTitle = displayName ? `${displayName} Dashboard` : "Dashboard";

  return (
    <div className={cn("flex flex-col gap-4", stickyTitle && "contents")}>
      <div
        data-slot="registrant-dashboard-title"
        className={cn(
          "flex items-start justify-between gap-4 bg-background",
          // Opaque band with a hard edge: tiles scroll under it, and without the
          // border they read as overlapping the title instead of passing behind.
          stickyTitle &&
            "sticky top-0 z-50 mb-4 border-b py-3 shadow-[0_4px_6px_-6px_hsl(var(--foreground)/0.35)]",
        )}
      >
        <div>
          <h2 className="text-3xl font-bold tracking-tight">
            {dashboardTitle}
          </h2>
          <p className="text-muted-foreground">
            Manage your property registrations
          </p>
        </div>
        <ContextHelpButton articleId={helpArticleId} helpPortal={helpPortal} />
      </div>

      {/* Registration and Balance Due - mobile */}
      <div
        className={cn("md:hidden flex flex-col gap-3", stickyTitle && "mb-4")}
      >
        {complianceIssueCount > 0 && (
          <ComplianceIssuesTile
            count={complianceIssueCount}
            onView={
              onViewComplianceIssueRegistrations || onSelectWorkspaceTab
                ? () =>
                    onViewComplianceIssueRegistrations
                      ? onViewComplianceIssueRegistrations()
                      : onSelectWorkspaceTab?.("compliance-issues")
                : undefined
            }
            className="w-full"
          />
        )}
        {needsAttentionCount > 0 && (
          <Card className="w-full border-warning bg-warning/5">
            <CardHeader className="pb-2">
              <PortalTileTitle title="Needs Attention" className="text-lg" />
            </CardHeader>
            <CardContent className="pt-0 flex flex-col gap-2">
              <p className="text-sm text-muted-foreground">
                {needsAttentionCount} registration
                {needsAttentionCount === 1 ? "" : "s"} need
                {needsAttentionCount === 1 ? "s" : ""} attention
              </p>
              {onViewNeedsAttentionRegistrations || onSelectWorkspaceTab ? (
                <Button
                  variant="outline"
                  className="w-full"
                  type="button"
                  onClick={() =>
                    onViewNeedsAttentionRegistrations
                      ? onViewNeedsAttentionRegistrations()
                      : onSelectWorkspaceTab?.("needs-attention")
                  }
                >
                  <ClipboardList className="h-4 w-4 mr-2" />
                  View properties
                </Button>
              ) : null}
            </CardContent>
          </Card>
        )}
        {!hideBalanceDue && balanceDueCount > 0 && (
          <Card className="w-full">
            <CardHeader className="pb-2">
              <PortalTileTitle title="Balance Due" className="text-lg" />
            </CardHeader>
            <CardContent className="pt-0 flex flex-col gap-2">
              <p className="text-sm text-muted-foreground">
                {balanceDueCount} propert
                {balanceDueCount === 1 ? "y" : "ies"} with a balance due
              </p>
              {onViewBalanceDueRegistrations ? (
                <Button
                  variant="outline"
                  className="w-full"
                  type="button"
                  onClick={() => onViewBalanceDueRegistrations()}
                >
                  <ClipboardList className="h-4 w-4 mr-2" />
                  View properties
                </Button>
              ) : null}
              {canMakePayments ? (
                <Link to="/registrant/bulk-payment">
                  <Button className="w-full">
                    <CreditCard className="h-4 w-4 mr-2" />
                    Pay now
                  </Button>
                </Link>
              ) : null}
            </CardContent>
          </Card>
        )}
        {inspectionPendingCount > 0 && (
          <InspectionPendingTile
            count={inspectionPendingCount}
            onView={onViewInspectionPendingRegistrations}
            className="w-full"
          />
        )}
        {bulkPaymentPayLaterCount > 0 && (
          <Card className="w-full">
            <CardHeader className="pb-2">
              <PortalTileTitle title="Bulk Payment" className="text-lg" />
            </CardHeader>
            <CardContent className="pt-0 flex flex-col gap-2">
              <p className="text-sm text-muted-foreground">
                {`${bulkPaymentPayLaterCount} propert${
                  bulkPaymentPayLaterCount === 1 ? "y" : "ies"
                } scheduled to be paid later`}
              </p>
              {onViewBulkPaymentPayLaterRegistrations ? (
                <Button
                  variant="outline"
                  className="w-full"
                  type="button"
                  onClick={() => onViewBulkPaymentPayLaterRegistrations()}
                >
                  <ClipboardList className="h-4 w-4 mr-2" />
                  View properties
                </Button>
              ) : null}
              {canMakePayments ? (
                <Link to="/registrant/bulk-payment">
                  <Button className="w-full">
                    <CreditCard className="h-4 w-4 mr-2" />
                    Pay now
                  </Button>
                </Link>
              ) : null}
            </CardContent>
          </Card>
        )}
        {showRegisterButton &&
          !areLicenseTypesLoading &&
          (licenseTypes.length === 1 ? (
            // Single license type: simple button
            <Link
              to="/registrant/new-license"
              search={{
                licenseTypeId: licenseTypes[0].id,
                jurisdictionId: undefined,
              }}
            >
              <Button className="w-full">
                <PlusCircle className="h-4 w-4 mr-2" />
                Register {licenseTypes[0].name}
              </Button>
            </Link>
          ) : (
            // Multiple license types: card with buttons
            <Card className="w-full">
              <CardHeader className="pb-2">
                <PortalTileTitle title="New Registration" className="text-lg" />
              </CardHeader>
              <CardContent className="pt-0">
                <div className="flex flex-col gap-2">
                  {licenseTypes.map((licenseType) => (
                    <Link
                      key={licenseType.id}
                      to="/registrant/new-license"
                      search={{
                        licenseTypeId: licenseType.id,
                        jurisdictionId: undefined,
                      }}
                    >
                      <Button
                        variant="outline"
                        className="w-full justify-start"
                      >
                        <PlusCircle className="h-4 w-4 mr-2" />
                        {licenseType.name}
                      </Button>
                    </Link>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
      </div>

      {/* md+: filters wrap horizontally first; tile strip stays nowrap until this row wraps */}
      <div
        className={cn(
          "flex flex-col gap-4 md:flex-row md:flex-wrap md:items-start md:gap-x-6 md:gap-y-4",
          // `contents` above drops this row's parent box, so the surrounding
          // space-y (which margins all but the last child) never reaches it.
          stickyTitle && "mb-8",
        )}
      >
        <div className="w-full min-w-0 md:min-w-64 md:flex-1 md:basis-0 md:max-w-full">
          <DashboardFilters
            filterDropdownsLayout="wrap-row"
            activeFiltersLayout="single-line"
            selectedJurisdictions={selectedJurisdictions}
            onSelectedJurisdictionsChange={onSelectedJurisdictionsChange}
            jurisdictionOptions={jurisdictionOptions}
            showJurisdictionFilter={
              !!(
                selectedJurisdictions &&
                onSelectedJurisdictionsChange &&
                jurisdictionOptions
              )
            }
            selectedLicenseTypes={selectedLicenseTypes}
            onSelectedLicenseTypesChange={onSelectedLicenseTypesChange}
            licenseTypeOptions={licenseTypeOptions}
            showLicenseTypeFilter={shouldShowLicenseTypeFilter}
            hasMultipleRegistrantAccounts={hasMultipleRegistrantAccounts}
            registrantOptions={registrantOptions}
            selectedRegistrantAccountId={selectedRegistrantAccountId}
            onSelectedRegistrantAccountIdChange={
              onSelectedRegistrantAccountIdChange
            }
            registrantOptionsCriteria="Registrant choices include active users only."
            columnFilters={columnFilters}
            onColumnFiltersChange={onColumnFiltersChange}
            registrantBalanceDueCohortActive={registrantBalanceDueCohortActive}
            onClearRegistrantBalanceDueCohort={
              onClearRegistrantBalanceDueCohort
            }
            registrantBulkPaymentPayLaterCohortActive={
              registrantBulkPaymentPayLaterCohortActive
            }
            onClearRegistrantBulkPaymentPayLaterCohort={
              onClearRegistrantBulkPaymentPayLaterCohort
            }
            selectedAms={selectedAms}
            onSelectedAmsChange={onSelectedAmsChange}
            amsOptions={amsOptions}
          />
        </div>

        {/* Registration and Balance Due - desktop; nowrap so tiles do not break up before the row wraps */}
        <div className="hidden md:flex md:shrink-0 md:flex-nowrap md:items-stretch md:gap-3">
          {complianceIssueCount > 0 && (
            <ComplianceIssuesTile
              count={complianceIssueCount}
              onView={
                onViewComplianceIssueRegistrations || onSelectWorkspaceTab
                  ? () =>
                      onViewComplianceIssueRegistrations
                        ? onViewComplianceIssueRegistrations()
                        : onSelectWorkspaceTab?.("compliance-issues")
                  : undefined
              }
              className="w-auto min-w-[200px]"
            />
          )}
          {needsAttentionCount > 0 && (
            <Card className="w-auto min-w-[200px] border-warning bg-warning/5">
              <CardHeader className="pb-2">
                <PortalTileTitle title="Needs Attention" className="text-lg" />
              </CardHeader>
              <CardContent className="pt-0 flex flex-col gap-2">
                <p className="text-sm text-muted-foreground">
                  {needsAttentionCount} registration
                  {needsAttentionCount === 1 ? "" : "s"} need
                  {needsAttentionCount === 1 ? "s" : ""} attention
                </p>
                {onViewNeedsAttentionRegistrations || onSelectWorkspaceTab ? (
                  <Button
                    variant="outline"
                    className="w-full"
                    type="button"
                    onClick={() =>
                      onViewNeedsAttentionRegistrations
                        ? onViewNeedsAttentionRegistrations()
                        : onSelectWorkspaceTab?.("needs-attention")
                    }
                  >
                    <ClipboardList className="h-4 w-4 mr-2" />
                    View properties
                  </Button>
                ) : null}
              </CardContent>
            </Card>
          )}
          {!hideBalanceDue && balanceDueCount > 0 && (
            <Card className="w-auto min-w-[200px]">
              <CardHeader className="pb-2">
                <PortalTileTitle title="Balance Due" className="text-lg" />
              </CardHeader>
              <CardContent className="pt-0 flex flex-col gap-2">
                <p className="text-sm text-muted-foreground">
                  {balanceDueCount} propert
                  {balanceDueCount === 1 ? "y" : "ies"} with a balance due
                </p>
                {onViewBalanceDueRegistrations ? (
                  <Button
                    variant="outline"
                    className="w-full"
                    type="button"
                    onClick={() => onViewBalanceDueRegistrations()}
                  >
                    <ClipboardList className="h-4 w-4 mr-2" />
                    View properties
                  </Button>
                ) : null}
                {canMakePayments ? (
                  <Link to="/registrant/bulk-payment">
                    <Button className="w-full">
                      <CreditCard className="h-4 w-4 mr-2" />
                      Pay now
                    </Button>
                  </Link>
                ) : null}
              </CardContent>
            </Card>
          )}
          {inspectionPendingCount > 0 && (
            <InspectionPendingTile
              count={inspectionPendingCount}
              onView={onViewInspectionPendingRegistrations}
              className="w-auto min-w-[200px]"
            />
          )}
          {bulkPaymentPayLaterCount > 0 && (
            <Card className="w-auto min-w-[200px]">
              <CardHeader className="pb-2">
                <PortalTileTitle title="Bulk Payment" className="text-lg" />
              </CardHeader>
              <CardContent className="pt-0 flex flex-col gap-2">
                <p className="text-sm text-muted-foreground">
                  {`${bulkPaymentPayLaterCount} propert${
                    bulkPaymentPayLaterCount === 1 ? "y" : "ies"
                  } scheduled to be paid later`}
                </p>
                {onViewBulkPaymentPayLaterRegistrations ? (
                  <Button
                    variant="outline"
                    className="w-full"
                    type="button"
                    onClick={() => onViewBulkPaymentPayLaterRegistrations()}
                  >
                    <ClipboardList className="h-4 w-4 mr-2" />
                    View properties
                  </Button>
                ) : null}
                {canMakePayments ? (
                  <Link to="/registrant/bulk-payment">
                    <Button className="w-full">
                      <CreditCard className="h-4 w-4 mr-2" />
                      Pay now
                    </Button>
                  </Link>
                ) : null}
              </CardContent>
            </Card>
          )}
          {showRegisterButton &&
            !areLicenseTypesLoading &&
            (licenseTypes.length === 1 ? (
              // Single license type: simple button
              <Link
                to="/registrant/new-license"
                search={{
                  licenseTypeId: licenseTypes[0].id,
                  jurisdictionId: undefined,
                }}
              >
                <Button className="w-auto">
                  <PlusCircle className="h-4 w-4 mr-2" />
                  Register {licenseTypes[0].name}
                </Button>
              </Link>
            ) : (
              // Multiple license types: card with buttons
              <Card className="w-auto min-w-[300px]">
                <CardHeader className="pb-2">
                  <PortalTileTitle
                    title="New Registration"
                    className="text-lg"
                  />
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="flex flex-col gap-2">
                    {licenseTypes.map((licenseType) => (
                      <Link
                        key={licenseType.id}
                        to="/registrant/new-license"
                        search={{
                          licenseTypeId: licenseType.id,
                          jurisdictionId: undefined,
                        }}
                      >
                        <Button
                          variant="outline"
                          className="w-full justify-start"
                        >
                          <PlusCircle className="h-4 w-4 mr-2" />
                          {licenseType.name}
                        </Button>
                      </Link>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
        </div>
      </div>
    </div>
  );
}
